import { useEffect, useRef, useState } from 'react';
import Breadcrumbs from '../common/Breadcrumbs';
import RichContent from '../common/RichContent';
import { publishAppToast } from '../../utils/appToast';

const STAR_PATH =
  'M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z';

async function copyTextToClipboard(text) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

function ShareButton() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  const handleCopyLink = async () => {
    const url = window.location.href;

    try {
      await copyTextToClipboard(url);
      publishAppToast('Link copied to clipboard', 'success');
      setOpen(false);
    } catch {
      publishAppToast('Failed to copy link', 'error');
    }
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        className="flex h-8 w-8 items-center justify-center rounded-full bg-[#1A87CC] text-white transition hover:bg-[#1578b8]"
        aria-label="Share course"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
          />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-30 mt-2 min-w-[160px] overflow-hidden rounded-xl border border-white/10 bg-white py-1 shadow-[0_8px_30px_rgba(0,0,0,0.2)]"
        >
          <button
            type="button"
            role="menuitem"
            onClick={handleCopyLink}
            className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-sm font-medium text-[#1B1B1B] transition hover:bg-[#F3F7FB]"
          >
            <svg className="h-4 w-4 text-[#1A87CC]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-2.287a4.5 4.5 0 00-1.242-7.244l-4.5-4.5a4.5 4.5 0 00-6.364 6.364L4.34 8.11"
              />
            </svg>
            Copy link
          </button>
        </div>
      )}
    </div>
  );
}

function CourseDetailHero({ course }) {
  const displayRating = Number.isInteger(course.rating)
    ? String(course.rating)
    : course.rating.toFixed(1);

  return (
    <section className="relative min-h-[320px] overflow-hidden bg-black pb-20 sm:min-h-[360px] lg:min-h-[380px] lg:pb-24">
      {course.heroImage && (
        <img
          src={course.heroImage}
          alt=""
          className="absolute inset-y-0 right-0 hidden h-full w-[62%] max-w-[760px] object-cover object-top grayscale md:block"
          style={{
            maskImage:
              'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 22%, rgba(0,0,0,0.85) 42%, black 58%, black 100%)',
            WebkitMaskImage:
              'linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 22%, rgba(0,0,0,0.85) 42%, black 58%, black 100%)',
          }}
        />
      )}

      <div className="relative z-10 mx-auto max-w-7xl px-4 pb-2 pt-6 sm:px-6 sm:pt-8 lg:px-8">
        <Breadcrumbs items={course.breadcrumbs} variant="dark" />

        <div className="mt-5 max-w-[640px]">
          {(course.popular || course.badge) && (
            <span className="inline-block rounded bg-[#80651D] px-2.5 py-1 text-[14px] font-medium text-[#ffffff]">
              {course.popular ? 'Popular' : course.badge}
            </span>
          )}

          <h1 className="mt-2 text-[26px] font-bold leading-tight text-[#9369CC] sm:text-[32px] lg:text-[36px]">
            {course.title}
          </h1>

          <RichContent
            content={course.description}
            className="mt-3 text-white/70 sm:text-[15px] [&_a]:text-white/90 [&_p]:mb-0 [&_strong]:text-white [&_b]:text-white"
          />

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              <span className="text-base font-bold text-white">{displayRating}</span>
              <div className="flex gap-0.5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <svg
                    key={star}
                    className="h-4 w-4 text-[#F5C842]"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    aria-hidden
                  >
                    <path d={STAR_PATH} />
                  </svg>
                ))}
              </div>
            </div>

            <span className="rounded-full bg-[#3A3A3A] px-3 py-1 text-xs font-medium text-white">
              {course.classTag}
            </span>
            <span
              className="rounded-full px-3 py-1 text-xs font-bold uppercase"
              style={{
                backgroundColor: course.subjectTagStyle?.backgroundColor || '#1A87CC',
                color: course.subjectTagTextColor || '#FFFFFF',
              }}
            >
              {course.subjectTag}
            </span>

            <ShareButton />
          </div>

          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-xs text-white/45 sm:text-sm">
            <span>
              Created By : <span className="text-white/75">{course.createdBy}</span>
            </span>
            <span>
              Last Updated : <span className="text-white/75">{course.lastUpdated}</span>
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default CourseDetailHero;
